/*
 * Milestone 5: mail domains, mailboxes, aliases and a server's mail role
 * (docs/milestone-5.md, "Panel (API and web)"; docs/api.md, "Mail (milestone 5)").
 * Types and hooks live together so the shapes are easy to follow.
 */
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrg } from "../app/org";
import { api, qs } from "./client";
import { keys } from "./keys";
import { watchOperation, type WatchMessage } from "./live";
import type { ServerSummary } from "./types";

/* ── Types ──────────────────────────────────────────────────────── */

/** unknown: the lookup itself failed (timeout, SERVFAIL); not a verdict. */
export type DnsCheckStatus = "ok" | "missing" | "wrong" | "unknown";

/** One record to set, with what the live lookup found. */
export type MailDnsRecord = {
  type: "MX" | "TXT" | "PTR" | "A";
  /** The full name: "example.com", "hx202610._domainkey.example.com", "_dmarc.example.com"; for PTR, the server's IP. */
  name: string;
  /** The exact value to set. */
  value: string;
  priority?: number;
  /** "SPF", "DKIM", "DMARC", "Reverse DNS". */
  purpose?: string;
  /** Where it's set, when that isn't the domain's DNS ("the server's host"). */
  setAt?: string;
  /** null: not checked yet. */
  status: DnsCheckStatus | null;
  /** What the lookup found, as the resolver returned it. */
  found: string[];
  /** "MX points here", "SPF is missing this server". */
  detail: string | null;
};

/** The list's DNS state: each record's status, or null before the first check. */
export type MailDnsState = {
  mx: DnsCheckStatus | null;
  spf: DnsCheckStatus | null;
  dkim: DnsCheckStatus | null;
  dmarc: DnsCheckStatus | null;
  ptr: DnsCheckStatus | null;
  allOk: boolean;
  /** MX resolves to this server and port 25 answers. */
  readyToReceive: boolean;
  checkedAt: string | null;
} | null;

export type MailForwarding = { to: string[]; keepCopy: boolean };
export type MailVacation = { subject: string; body: string; from: string | null; until: string | null; days: number };

export type FilterField = "from" | "to" | "cc" | "subject" | "list-id";
export type FilterOp = "contains" | "is" | "starts" | "ends";
export type FilterAction =
  | { type: "move"; folder: string }
  | { type: "markRead" }
  | { type: "flag" }
  | { type: "forward"; to: string }
  | { type: "discard" };
export type MailFilter = {
  id: string;
  name: string;
  enabled: boolean;
  match: "all" | "any";
  conditions: { field: FilterField; op: FilterOp; value: string }[];
  actions: FilterAction[];
  stop: boolean;
};

/** How a mail app connects. null while the server has no mail name. */
export type MailConnection = {
  username: string;
  imap: { host: string; port: number; security: string };
  smtp: { host: string; port: number; security: string };
  smtpAlternative?: { host: string; port: number; security: string } | null;
} | null;

export type Mailbox = {
  id: string;
  domainId: string;
  address: string;
  localPart: string;
  displayName: string | null;
  /** 0: unlimited. */
  quotaMb: number;
  usageBytes: number | null;
  messages: number | null;
  /** From webmail and mail apps (IMAP) alike. */
  lastLoginAt: string | null;
  enabled: boolean;
  /** false: receive only, or sending suspended. */
  canSend: boolean;
  forwarding: MailForwarding | null;
  vacation: MailVacation | null;
  filters: MailFilter[];
  blocked: string[];
  createdAt: string;
  updatedAt?: string | null;
  /** Deleted: its mail is kept until purgeAfter. */
  deleted: { at: string; purgeAfter: string | null; purgedAt: string | null } | null;
  connection: MailConnection;
  /** When it last hit the hourly sending limit, when the API says. */
  limitedAt?: string | null;
};

/** Older name, for the pieces that only need the summary fields. */
export type MailboxSummary = Mailbox;

export type MailAlias = { id: string; domainId?: string; localPart: string; address: string; targets: string[]; createdAt?: string };

export type MailSpam = { junkScore: number | null; rejectScore: number | null; allow: string[]; block: string[] };

export type MailTraffic = { received: number; sent: number; rejected: number; spam: number; bounced: number; deferred: number };

/** One line of the server's mail log: a message to or from the domain, and what became of it. */
export type MailDelivery = {
  id: string;
  at: string;
  direction: "in" | "out";
  from: string;
  to: string;
  status: "delivered" | "deferred" | "bounced" | "rejected" | "spam";
  detail: string;
};

export type MailDomainSummary = {
  id: string;
  domain: string;
  /** Null, with the name, for people who don't see that server. */
  serverId: string | null;
  /** Null for people who don't see servers (docs/plans.md). */
  serverName: string | null;
  /** The server's mail name. */
  hostname: string | null;
  site: { id: string; name: string } | null;
  enabled: boolean;
  catchAll: string | null;
  mailboxCount: number;
  aliasCount: number;
  dns: MailDnsState;
  last24h: MailTraffic;
  createdAt: string;
  deleted?: { at: string; purgeAfter: string | null } | null;
};

export type MailDomainDetail = MailDomainSummary & {
  dkim: { selector: string; publicKey: string };
  spam: MailSpam;
  records: MailDnsRecord[];
  /** Why the last check couldn't run, if it couldn't. */
  dnsError: string | null;
  /** A change is waiting to be sent to the server (changes are batched for 2 seconds). */
  applying: boolean;
  lastApply: { operationId: string; status: string; error: string | null; at: string | null } | null;
  mailboxes: Mailbox[];
  aliases: MailAlias[];
  /** Daily, the last 14 days. */
  traffic: (MailTraffic & { day: string })[];
  /** Hosterr runs the zone and this person may set its records ("Set these records for me"); orgId is the zone's. */
  oneClick?: { orgId: string; domainId: string; zone: string } | null;
};

export type MailRole = "web" | "mail";

/**
 * waiting_dns: the server's mail name doesn't point to it yet, so nothing was installed.
 * applying: a settings change was sent and isn't confirmed yet; mail keeps running on the settings in force.
 */
export type ServerMailStatus = "waiting_dns" | "setting_up" | "ready" | "applying" | "failed";

/** The settings a server's mail runs with: in force, or `pending` while a change is applying. */
export type ServerMailSettings = {
  /** Where bounces and mail to postmaster@ and abuse@ of every domain go. */
  postmaster: string;
  /** true: the installation's address, not one set for this server. */
  postmasterIsDefault: boolean;
  relay: { host: string; port: number; user: string } | null;
  limits: { messageSizeMb: number; recipientsPerMessage: number; sendPerMailboxPerHour: number };
  spam: { junkScore: number; rejectScore: number };
  fullTextSearch: boolean;
};

export type ServerMail = ServerMailSettings & {
  status: ServerMailStatus;
  statusDetail: string | null;
  hostname: string;
  /** The A record the mail name needs. */
  dnsRecord: { type: "A"; name: string; value: string | null };
  /** A change sent but not confirmed: the settings as they'll be once it succeeds. */
  pending?: (ServerMailSettings & { operationId: string; requestedAt: string }) | null;
  versions: Record<string, string> | null;
  certificate: { notAfter: string; names: string[] } | null;
  listening: number[] | null;
  setupAt: string | null;
  reverseDns: { status: "set" | "failed" | "not_managed"; value: string | null; detail: string | null; at: string } | null;
  /** From the hourly audit. */
  queue: { active: number; deferred: number; hold: number; oldestAt: string | null } | null;
  /** Ids are matched when the audit was stored (null: not a live mailbox here). */
  limited: { address: string; at: string; detail: string; mailboxId?: string | null; domainId?: string | null }[];
  auditedAt: string | null;
};

/** What the servers list carries. */
export type ServerMailSummary = {
  status: ServerMailStatus;
  statusDetail: string | null;
  hostname: string;
  /** From the last hourly audit; null until there is one. */
  queue?: { active: number; deferred: number; oldestAt: string | null } | null;
  certificate?: { notAfter: string } | null;
  limitedCount?: number;
};

export type MailBackup = { id: string; at: string; kind: string; reason: string | null; domainBytes: number | null };

/* ── Keys ───────────────────────────────────────────────────────── */

export const mailKeys = {
  all: (orgId: string) => ["org", orgId, "mail"] as const,
  domains: (orgId: string) => ["org", orgId, "mail", "domains"] as const,
  domain: (orgId: string, id: string) => ["org", orgId, "mail", "domain", id] as const,
  mailbox: (orgId: string, id: string) => ["org", orgId, "mail", "mailbox", id] as const,
};

const m = (orgId: string) => `/orgs/${orgId}/mail`;

/* ── Reads ──────────────────────────────────────────────────────── */

/**
 * With `mailboxes`, each domain carries its mailboxes too (the Mail page lists them all). With `accounts`, every
 * account's mail domains too (a platform admin on the platform org; app/accounts.tsx).
 */
export function useMailDomains(enabled = true, opts: { mailboxes?: boolean; accounts?: boolean } = {}) {
  const org = useOrg();
  const query = [opts.mailboxes ? "mailboxes=1" : "", opts.accounts ? "accounts=all" : ""].filter(Boolean).join("&");
  return useQuery({
    queryKey: [...mailKeys.domains(org.id), opts.mailboxes ? "with-mailboxes" : "plain", ...(opts.accounts ? ["accounts"] : [])] as const,
    queryFn: ({ signal }) => api.get<{ domains: (MailDomainSummary & { mailboxes?: Mailbox[] })[] }>(`${m(org.id)}/domains${query ? `?${query}` : ""}`, signal),
    select: (d) => d.domains,
    enabled,
    staleTime: 30_000,
    refetchInterval: opts.accounts ? 60_000 : undefined,
  });
}

const domainQuery = (orgId: string, id: string) => ({
  queryKey: mailKeys.domain(orgId, id),
  queryFn: ({ signal }: { signal: AbortSignal }) => api.get<{ domain: MailDomainDetail }>(`${m(orgId)}/domains/${id}`, signal),
  select: (d: { domain: MailDomainDetail }) => d.domain,
  staleTime: 15_000,
});

export function useMailDomain(id: string) {
  const org = useOrg();
  return useQuery({ ...domainQuery(org.id, id), enabled: !!id });
}

/** Several domains' details at once (the Mail page lists every domain's mailboxes). */
export function useMailDomainDetails(ids: string[]) {
  const org = useOrg();
  return useQueries({ queries: ids.map((id) => domainQuery(org.id, id)) });
}

export function useMailbox(id: string | null) {
  const org = useOrg();
  return useQuery({
    queryKey: mailKeys.mailbox(org.id, id ?? "none"),
    queryFn: ({ signal }) => api.get<{ mailbox: Mailbox }>(`${m(org.id)}/mailboxes/${id}`, signal),
    select: (d) => d.mailbox,
    enabled: !!id,
  });
}

/** The mailbox domain's mail backups, newest first, for "Restore from backup". */
export function useMailboxBackups(mailboxId: string | null) {
  const org = useOrg();
  return useQuery({
    queryKey: [...mailKeys.all(org.id), "backups", mailboxId ?? "none"] as const,
    queryFn: ({ signal }) => api.get<{ backups: MailBackup[] }>(`${m(org.id)}/mailboxes/${mailboxId}/backups`, signal),
    select: (d) => d.backups,
    enabled: !!mailboxId,
  });
}

export type MailDeliveryStatus = MailDelivery["status"];
/** For the filter menu: each option counted against the rest of what's chosen; `all`, what the filters match now. */
export type MailDeliveryCounts = { all: number; status: Record<MailDeliveryStatus, number>; direction: Record<MailDelivery["direction"], number> };
export type MailDeliveriesFilter = { q?: string; status?: string[]; direction?: string[]; /** A mailbox id: its mail only. */ mailbox?: string | null; /** ISO instants, `to` exclusive. */ from?: string | null; to?: string | null };
type DeliveriesPage = { deliveries: MailDelivery[]; next: string | null; counts: MailDeliveryCounts; keptDays: number };

/** A mail domain's deliveries (docs/api.md, "Deliveries"), searched and filtered on the server, a page at a time. */
export function useMailDeliveries(domainId: string, f: MailDeliveriesFilter, limit = 50) {
  const org = useOrg();
  const params = {
    q: f.q?.trim().slice(0, 200) || undefined,
    status: f.status?.join(",") || undefined,
    direction: f.direction?.join(",") || undefined,
    mailbox: f.mailbox ?? undefined,
    from: f.from ?? undefined,
    to: f.to ?? undefined,
    limit,
  };
  const q = useInfiniteQuery({
    queryKey: [...mailKeys.domain(org.id, domainId), "deliveries", params] as const,
    queryFn: ({ signal, pageParam }) => api.get<DeliveriesPage>(`${m(org.id)}/domains/${domainId}/deliveries${qs({ ...params, before: pageParam ?? undefined })}`, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    placeholderData: (prev) => prev, // the list stays while a new search loads
    retry: false,
    refetchInterval: 5 * 60_000, // the server's log is read hourly
  });
  const first = q.data?.pages[0];
  return {
    deliveries: (q.data?.pages ?? []).flatMap((p) => p.deliveries),
    counts: first?.counts ?? null,
    keptDays: first?.keptDays ?? 14,
    isPending: q.isPending,
    isFetching: q.isFetching && !q.isFetchingNextPage,
    error: q.isError ? q.error : null,
    hasMore: !!q.hasNextPage,
    loadingMore: q.isFetchingNextPage,
    loadMore: () => void q.fetchNextPage(),
    refetch: () => void q.refetch(),
  };
}

/**
 * Whether a mail server's hourly audit has run (traffic, deliveries, sign-ins and usage come from it).
 * From the servers list: its queue is null until the first audit. undefined while unknown.
 */
export function useMailAudited(serverId: string | null | undefined): boolean | undefined {
  const org = useOrg();
  const q = useQuery({
    queryKey: keys.servers(org.id),
    queryFn: ({ signal }) => api.get<{ servers: ServerSummary[] }>(`/orgs/${org.id}/servers`, signal),
    select: (d) => d.servers,
    enabled: !!serverId,
  });
  const s = q.data?.find((x) => x.id === serverId);
  if (!s?.mail || s.mail.queue === undefined) return undefined;
  return s.mail.queue !== null;
}

/* ── Writes ─────────────────────────────────────────────────────── */

/** Mail writes refresh every mail query (lists, domains, mailboxes). */
function useMailWrite<V, R>(fn: (orgId: string, v: V) => Promise<R>, success?: WatchMessage) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(org.id, v),
    onSuccess: (res) => {
      const op = (res as { operationId?: string | null } | null)?.operationId;
      if (op) watchOperation(op, success);
      void qc.invalidateQueries({ queryKey: mailKeys.all(org.id) });
      void qc.invalidateQueries({ queryKey: keys.operationLists(org.id) });
    },
  });
}

export function useAddMailDomain() {
  // serverId: left out, Hosterr picks a mail server (docs/plans.md); only people who see servers choose one.
  return useMailWrite((orgId, v: { domain: string; serverId?: string; siteId?: string | null }) =>
    api.post<{ domain: MailDomainDetail }>(`${m(orgId)}/domains`, v),
  );
}

export function useCheckMailDns() {
  return useMailWrite((orgId, domainId: string) => api.post<{ domain: MailDomainDetail }>(`${m(orgId)}/domains/${domainId}/dns-check`, {}));
}

export function useUpdateMailDomain() {
  return useMailWrite((orgId, v: { domainId: string; catchAll?: string | null; spam?: Partial<MailSpam>; siteId?: string | null; enabled?: boolean }) => {
    const { domainId, ...body } = v;
    return api.patch<{ domain: MailDomainDetail }>(`${m(orgId)}/domains/${domainId}`, body);
  });
}

export function useCreateMailbox() {
  return useMailWrite((orgId, v: { domainId: string; localPart: string; displayName?: string | null; password: string; quotaMb: number }) => {
    const { domainId, ...body } = v;
    return api.post<{ mailbox: Mailbox }>(`${m(orgId)}/domains/${domainId}/mailboxes`, body);
  });
}

export type MailboxPatch = Partial<Pick<Mailbox, "displayName" | "quotaMb" | "canSend" | "enabled" | "forwarding" | "vacation" | "filters" | "blocked">> & { password?: string };

export function useUpdateMailbox() {
  return useMailWrite((orgId, v: { mailboxId: string } & MailboxPatch) => {
    const { mailboxId, ...body } = v;
    return api.patch<{ mailbox: Mailbox }>(`${m(orgId)}/mailboxes/${mailboxId}`, body);
  });
}

/** "Remove mail for <domain>": a restore point of its mail first; stored mail is kept 30 days. */
export function useDeleteMailDomain() {
  return useMailWrite((orgId, v: { domainId: string; confirmName: string; restorePoint?: boolean }) =>
    api.del<{ operationId: string | null; restorePoint: { backupId: string; operationId: string } | null }>(
      `${m(orgId)}/domains/${v.domainId}`,
      { confirmName: v.confirmName, ...(v.restorePoint === false ? { restorePoint: false } : {}) },
    ),
  );
}

/** A restore point of its mail first; the mail is kept 30 days. */
export function useDeleteMailbox() {
  return useMailWrite((orgId, v: { mailboxId: string; restorePoint?: boolean }) =>
    api.del<{ operationId: string | null; restorePoint: { backupId: string; operationId: string } | null }>(
      `${m(orgId)}/mailboxes/${v.mailboxId}`,
      v.restorePoint === false ? { restorePoint: false } : {},
    ),
  );
}

/** Into a new folder ("Restored Oct 19"): it never overwrites. */
export function useRestoreMailbox() {
  return useMailWrite(
    (orgId, v: { mailboxId: string; backupId: string; folder: string | null }) =>
      api.post<{ operationId: string; folder: string }>(`${m(orgId)}/mailboxes/${v.mailboxId}/restore`, { backupId: v.backupId, folder: v.folder }),
    { title: "Mail restored", description: "It's in a new folder in the mailbox." },
  );
}

export function useSaveAlias() {
  return useMailWrite((orgId, v: { domainId: string; aliasId?: string; localPart: string; targets: string[] }) =>
    v.aliasId
      ? api.patch<{ alias: MailAlias }>(`${m(orgId)}/aliases/${v.aliasId}`, { targets: v.targets })
      : api.post<{ alias: MailAlias }>(`${m(orgId)}/domains/${v.domainId}/aliases`, { localPart: v.localPart, targets: v.targets }),
  );
}

export function useDeleteAlias() {
  return useMailWrite((orgId, aliasId: string) => api.del<{ ok: true }>(`${m(orgId)}/aliases/${aliasId}`, {}));
}

/* ── A server's mail role ───────────────────────────────────────── */

function useServerMailWrite<V, R>(serverId: string, fn: (orgId: string, v: V) => Promise<R>, success?: WatchMessage) {
  const org = useOrg();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.server(org.id, serverId) });
    void qc.invalidateQueries({ queryKey: keys.servers(org.id) });
    void qc.invalidateQueries({ queryKey: mailKeys.all(org.id) });
    void qc.invalidateQueries({ queryKey: keys.operationLists(org.id) });
  };
  return useMutation({
    mutationFn: (v: V) => fn(org.id, v),
    onSuccess: (res) => {
      const op = (res as { operationId?: string | null } | null)?.operationId;
      if (op) watchOperation(op, success);
      refresh();
    },
    // A 409 "dns_not_ready" still records the mail name and the record to add.
    onError: refresh,
  });
}

/**
 * "Add mail to this server", and "Check again" while its mail name doesn't
 * resolve yet: a 409 with code "dns_not_ready" says which record to add.
 */
export function useEnableMail(serverId: string) {
  return useServerMailWrite(serverId, (orgId, _: void) => api.post<{ operationId: string; mail: ServerMail }>(`/orgs/${orgId}/servers/${serverId}/mail`, {}), {
    title: "Mail is set up",
    description: "The server can send and receive mail now.",
  });
}

export function useUpdateServerMail(serverId: string) {
  return useServerMailWrite(
    serverId,
    (orgId, v: { postmaster?: string | null; relay?: { host: string; port: number; user: string; password?: string } | null; limits?: Partial<ServerMail["limits"]>; spam?: Partial<ServerMail["spam"]>; fullTextSearch?: boolean }) =>
      api.patch<{ mail: ServerMail; operationId: string | null }>(`/orgs/${orgId}/servers/${serverId}/mail`, v),
  );
}
